import { useEffect, useRef, useState } from 'react';

import { MILLISECONDS_PER_MINUTE } from 'lib/time';
import { AUTH_SESSION_CHANGED_EVENT } from 'services/auth/session';
import { APP_DATA_MUTATED_EVENT, AUTH_SESSION_EXPIRED_EVENT } from 'services/events';

const CACHE_TTL_MINUTES = 5;
const CACHE_TTL = CACHE_TTL_MINUTES * MILLISECONDS_PER_MINUTE;
const caches = new Map<string, { value: unknown; expiresAt: number }>();
let generation = 0;
const subscribers = new Set<() => void>();

export function invalidateDashboardData(): void {
  generation += 1;
  caches.clear();
  subscribers.forEach(refresh => refresh());
}

[
  APP_DATA_MUTATED_EVENT,
  AUTH_SESSION_CHANGED_EVENT,
  AUTH_SESSION_EXPIRED_EVENT,
  'education-priority-updated',
].forEach(event => window.addEventListener(event, invalidateDashboardData));
window.addEventListener('storage', event => {
  if (!event.key || event.key === 'accessToken' || event.key === 'refreshToken') {
    invalidateDashboardData();
  }
});

interface IResource<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

// Every resource shares the same invalidation and stale-response rules. Failed
// reads never enter the cache and never masquerade as a successful empty result.
export function useDashboardResource<T>(
  key: string | null,
  empty: T,
  load: () => Promise<T>,
): IResource<T> {
  const [version, setVersion] = useState(generation);
  const [result, setResult] = useState<{
    key: string;
    generation: number;
    data: T;
    error: string | null;
  } | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    const refresh = (): void => setVersion(generation);
    subscribers.add(refresh);
    refresh();
    return () => {
      subscribers.delete(refresh);
    };
  }, []);

  useEffect(() => {
    if (!key) return undefined;
    const requestGeneration = generation;
    const cached = caches.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      setResult({
        key,
        generation: requestGeneration,
        data: cached.value as T,
        error: null,
      });
      return undefined;
    }
    let cancelled = false;
    loadRef.current().then(
      data => {
        if (cancelled || generation !== requestGeneration) return;
        caches.set(key, { value: data, expiresAt: Date.now() + CACHE_TTL });
        setResult({ key, generation: requestGeneration, data, error: null });
      },
      () => {
        if (cancelled || generation !== requestGeneration) return;
        setResult({
          key,
          generation: requestGeneration,
          data: empty,
          error: 'dashboard.data-error',
        });
      },
    );
    return () => {
      cancelled = true;
    };
    // empty is a fallback value, not a resource identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version]);

  const current = result?.key === key && result?.generation === generation;
  return {
    data: current ? result!.data : empty,
    loading: Boolean(key) && !current,
    error: current ? result!.error : null,
  };
}

export async function fetchWithLimit<T>(
  factories: Array<() => Promise<T>>,
  limit: number,
): Promise<T[]> {
  const results = new Array<T>(factories.length);
  let next = 0;
  async function worker(): Promise<void> {
    while (next < factories.length) {
      const index = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      results[index] = await factories[index]();
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, factories.length) }, worker));
  return results;
}
